import { isString } from "es-toolkit";

import type { StringCondition, StringOperator } from "@skilitics/judy";
import { isReferencedValue, type ValueConfigFor } from "@skilitics/nautilus-core-designer-data";

import type { ConditionBuilder, ConditionBuilderContext } from "../../types";
import { ValueConfigSummaryText } from "../../values/forms/ValueConfigSummaryText.tsx";
import { StringConditionForm } from "./forms";
import { isStringOperator, operatorToEnglish } from "./helpers";
import { ConditionBuilderSummary } from "./ui/Summary";

export interface StringConditionContext extends ConditionBuilderContext {
  config: {
    operator?: StringOperator;
    value?: ValueConfigFor<"string">;
  };
  spec: {
    property: string;
  };

  // the assumption is that any property referenced by consuming node designer is a string
  result: Record<string, string>;
}

export type StringConditionBuilder = typeof stringConditionBuilder;

// generic so asks for a property name in spec
export const stringConditionBuilder: ConditionBuilder<StringConditionContext> = {
  typeId: "string",
  defaultCategory: "Variables",
  build: ({ config, spec: { property }, resultProperty, resolvableValue }) => {
    if (!stringConditionBuilder.isValid?.(config)) return;

    const { operator, value } = config;

    const object = resolvableValue(value, "string");
    if (object === undefined) return;

    return {
      type: "string",
      subject: resultProperty(property, "string"),
      operator,
      object,
    } as StringCondition;
  },
  isValid: ({ operator, value }) => isStringOperator(operator) && (isString(value) || isReferencedValue(value)),
  getInitialConfig: () => ({}),
  views: {
    summary: ({ spec: { title }, config: { operator, value } }) => (
      <ConditionBuilderSummary
        title={title}
        operator={operatorToEnglish(operator)}
        value={<ValueConfigSummaryText value={value} />}
      />
    ),
    edit: ({ spec, config, onConfigChange }) => <StringConditionForm {...{ spec, config, onConfigChange }} />,
  },
};
